import { useState } from "react";
import { App, Modal } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import {
  EditOutlined,
  ExclamationCircleOutlined,
  FileDoneOutlined,
  PrinterOutlined,
  StopOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import { useViewport } from "../../../../../hooks/useViewport";
import { usePaymentMethods } from "../../../../../hooks/usePaymentMethods";
import { computeVatBreakdown } from "../../../../../lib/vat";
import { R } from "../../orderingTheme";
import {
  DangerButton,
  GhostButton,
  ModalFooter,
  ModalHeader,
  PrimaryButton,
  SoftPrimaryButton,
} from "../../modalPrimitives";
import { useCancelInvoice } from "../../../../../hooks/useInvoices";
import { useAppSelector } from "../../../../../store/hooks";
import type { Invoice } from "../../../../../api/invoices";
import {
  ConfirmConsequences,
  type Consequence,
} from "../../../../../components/ConfirmConsequences";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { InvoiceCustomerEditModal } from "./InvoiceCustomerEditModal";
import { FiscalInvoicePreviewNotice } from "./FiscalInvoicePreviewNotice";
import { FiscalInvoiceCreditAction } from "./FiscalInvoiceCreditAction";
import { isCreditNote, isFiscalInvoiceDocument } from "../../../../../lib/fiscalInvoice";
import { InvoicePaperLines } from "./InvoicePaperLines";
import { InvoicePaperTotals } from "./InvoicePaperTotals";
import { toStoredLineVatInputs } from "./invoiceLines";
import { useInvoicePrint } from "./useInvoicePrint";
import { useInvoiceCancellationProtocol } from "../../../../../hooks/useInvoiceCancellationProtocol";
import { PrintPosConfirmModal } from "../reports/PrintPosConfirmModal";
import { blockIfLicenseReadOnly } from "../../../../../lib/licenseReadOnly";

interface Props {
  open: boolean;
  invoice: Invoice | null;
  onClose: () => void;
  // Бутонът „Анулирай" се показва само когато модалът се отваря от
  // справките за фактури. От маркиране (TableFooter) и от история на
  // сметките тук не искаме случайно да отвържем сметка от фактура.
  allowCancel?: boolean;
  // Моливът за редакция на получателя — също само от справките. Пипа само
  // снимката customer_* на записа; суми, редове, номер и дати са readonly.
  // И двете искат и правото `invoicing` (BE → 403 без него).
  allowEdit?: boolean;
  // Кредитното известие, което вече неутрализира тази фискална фактура —
  // само списъкът го знае (`credited_by` от GET /invoices); тогава
  // „Кредитно известие" го няма.
  creditedBy?: string | null;
}

const formatDate = (iso: string | null) => {
  if (!iso) return "—";
  try {
    return dayjs(iso).format("D.M.YYYY") + " г.";
  } catch {
    return "—";
  }
};

export const InvoicePreviewModal = ({
  open,
  invoice,
  onClose,
  allowCancel = false,
  allowEdit = false,
  creditedBy = null,
}: Props) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const { data: paymentMethods = [] } = usePaymentMethods();
  const { modal } = App.useApp();
  const message = useOrderingMessage();
  const cancelMutation = useCancelInvoice();
  // Печатът: оригиналът, докато не е отпечатан нито веднъж, после — само
  // дубликати, откъдето и да е отворен прегледът (`useInvoicePrint`).
  const { printAsDuplicate, isPrintingPos, printA4, printPos, originalPrintedQuestion } =
    useInvoicePrint(invoice);
  // Протоколът за анулиране — въпросът след „Анулирай" и бутонът на анулираната.
  const cancellationProtocol = useInvoiceCancellationProtocol(message);
  // „Анулирай" и редакцията на получателя — `invoicing` (BE `POST …/cancel`,
  // `PATCH …/customer`); справката се вижда и само с `view_invoices`.
  const permissions = useAppSelector((state) => state.auth.permissions);
  const canInvoice = (permissions ?? []).some((permission) => permission.name === "invoicing");
  // Редакция на получателя (моливът) — само от справките (allowEdit)
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);

  if (!invoice) return null;

  // Фискална фактура / кредитно известие: оригиналът е бонът от фискалното
  // устройство — без печат, анулиране и редакция на получателя (BE → 409);
  // лентата под хедъра казва защо (`FiscalInvoicePreviewNotice`).
  const fiscalDocument = isFiscalInvoiceDocument(invoice);
  // Фискалната фактура не се анулира — кредитира се (`FiscalInvoiceCreditAction`):
  // само жива, некредитирана, с една сметка (фискалната фактура е бонът на
  // сметката; фактурата на депозит е без сметка).
  const creditable =
    allowCancel &&
    fiscalDocument &&
    !isCreditNote(invoice) &&
    !invoice.cancelled_at &&
    !creditedBy &&
    (invoice.bill_ids?.length ?? 0) === 1;

  const handleCancelInvoice = () => {
    if (blockIfLicenseReadOnly()) return;
    // Анулирането е само документно (BE `POST /invoices/{id}/cancel` — soft):
    // сметките се освобождават, бонът и парите не се пипат — въпросът го казва.
    const billCount = invoice.bill_ids?.length ?? 0;
    const lines: Consequence[] = [
      { text: t("invoicePreview.cancelConfirmStays") },
      ...(billCount > 0
        ? [{ text: t("invoicePreview.cancelConfirmBills", { count: billCount }) }]
        : []),
      { text: t("invoicePreview.cancelConfirmNoPrint") },
      { text: t("invoicePreview.cancelConfirmFinal") },
    ];
    modal.confirm({
      title: t("invoicePreview.cancelConfirmTitle", { number: invoice.invoice_number }),
      icon: <ExclamationCircleOutlined style={{ color: R.brand }} />,
      content: (
        <ConfirmConsequences
          lines={lines}
          question={t("invoicePreview.cancelConfirmContent")}
        />
      ),
      okText: t("invoicePreview.cancel"),
      cancelText: t("common.cancel"),
      centered: true,
      okButtonProps: {
        style: { background: R.brand, borderColor: R.brand },
      },
      onOk: async () => {
        try {
          await cancelMutation.mutateAsync(invoice.id);
          message.success(t("invoicePreview.cancelSuccess"));
          onClose();
          cancellationProtocol.offer(invoice.id, invoice.invoice_number);
        } catch (error) {
          message.error(
            getApiErrorMessage(error, t("invoicePreview.cancelError")),
          );
        }
      },
    });
  };

  const paymentMethodName = paymentMethods.find(
    (pm) => pm.id === invoice.payment_method_id,
  )?.name_bg;

  const modalWidth = isMobile ? undefined : isTablet ? 720 : 880;

  // Споделена ДДС разбивка (lib/vat.ts) — същият алгоритъм като при издаване и
  // като на бекенда. Нетото/ДДС на ред сумират ТОЧНО на груповите тотали, а
  // те — на съхранените invoice.subtotal_taxable / vat_total.
  // Редът за бакшиш е извън ДДС групите (`kind: "tip"`).
  const breakdown = computeVatBreakdown(toStoredLineVatInputs(invoice.lines));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={modalWidth}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div
        style={{
          background: R.surface,
          display: "flex",
          flexDirection: "column",
          maxHeight: "92vh",
        }}
      >
        <style>{`
          /* Print: само .invoice-paper минава към принтера. Скриваме всичко
             друго на страницата (главното UI, header-а на модала, footer-а)
             и развиваме скрол-а на самата фактура, за да се отпечатат
             всички редове, а не само видимата част. */
          /* @page margin: 0 → НИКАКЪВ браузърски header/footer (дата,
             URL, page numbers). Горното поле на всяка страница го
             постигаме с table > thead spacer трик в JS (thead с
             display: table-header-group се повтаря на всяка отпечатана
             страница, а височината му създава top margin). */
          @page { size: A4; margin: 0; }
          @media print {
            body.printing-invoice > *:not(.invoice-print-wrapper) {
              display: none !important;
            }
            body.printing-invoice {
              margin: 0 !important;
              padding: 0 !important;
              background: #FFFFFF !important;
            }
            body.printing-invoice .invoice-print-wrapper {
              width: 100% !important;
              border-collapse: collapse !important;
            }
            body.printing-invoice .invoice-print-wrapper thead {
              display: table-header-group !important;
            }
            body.printing-invoice .invoice-print-wrapper thead th {
              height: 16mm !important;
              padding: 0 !important;
              border: 0 !important;
              background: #FFFFFF !important;
            }
            body.printing-invoice .invoice-print-wrapper td {
              padding: 0 !important;
              border: 0 !important;
              vertical-align: top !important;
            }
            /* Екранни контроли вътре в paper-а (моливът за редакция на
               получателя) не се печатат */
            body.printing-invoice .invoice-no-print {
              display: none !important;
            }
            body.printing-invoice .invoice-paper {
              position: static !important;
              max-height: none !important;
              overflow: visible !important;
              padding: 0 16mm 16mm 16mm !important;
              background: #FFFFFF !important;
              color: #1A1A1A !important;
            }
            /* Branding-ът върви веднага след съдържанието. С
               break-before:avoid + break-inside:avoid гарантираме, че
               НЕ се изолира на нова страница само за себе си. */
            body.printing-invoice .invoice-paper .invoice-branding {
              break-before: avoid !important;
              page-break-before: avoid !important;
              break-inside: avoid !important;
              page-break-inside: avoid !important;
            }
          }
        `}</style>
        <ModalHeader
          icon={<FileDoneOutlined />}
          title={t("invoicePreview.title", {
            number: invoice.invoice_number,
          })}
          subtitle={t("invoicePreview.subtitle", {
            date: formatDate(invoice.issue_date),
          })}
          onClose={onClose}
          isNarrow={isNarrow}
        />

        {fiscalDocument && (
          <FiscalInvoicePreviewNotice invoice={invoice} isNarrow={isNarrow} />
        )}

        <div
          className="invoice-paper"
          style={{
            position: "relative",
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: isNarrow ? "16px 18px" : "26px 32px",
            // Хартията е ВИНАГИ светла (печатен документ) — литерали,
            // не theme-реактивните R.* стойности.
            background: "#FFFFFF",
            color: "#241F1A",
            // Ползваме app font-а (Plus Jakarta Sans) — наследен от
            // <body>. Без override → preview-то изглежда консистентно с
            // header-а на модала и останалите страници.
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          {invoice.cancelled_at && (
            <div
              style={{
                position: "absolute",
                top: "40%",
                left: "50%",
                transform: "translate(-50%, -50%) rotate(-18deg)",
                fontSize: isNarrow ? 56 : 96,
                fontWeight: 900,
                letterSpacing: 6,
                color: "rgba(220,38,38,0.18)",
                border: "6px solid rgba(220,38,38,0.18)",
                padding: "10px 28px",
                borderRadius: 12,
                pointerEvents: "none",
                userSelect: "none",
                whiteSpace: "nowrap",
                zIndex: 1,
              }}
            >
              {t("invoicePreview.cancelledBadge")}
            </div>
          )}
          {/* Header card: Получател | Изпълнител */}
          <div
            style={{
              position: "relative",
              display: "grid",
              gridTemplateColumns: isNarrow ? "1fr" : "1fr 1fr",
              columnGap: 12,
              rowGap: 0,
              background: "#EDECE8",
              border: "1px solid #C9C7BF",
              padding: 14,
            }}
          >
            {/* Редакция на получателя — видим бутон горе вдясно на секцията;
                .invoice-no-print го маха от A4 печата */}
            {allowEdit && canInvoice && !invoice.cancelled_at && !fiscalDocument && (
              <button
                type="button"
                onClick={() => {
                  if (blockIfLicenseReadOnly()) return;
                  setIsEditingCustomer(true);
                }}
                className="invoice-no-print absolute right-2.5 top-2.5 inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-[10px] border border-pos-ctl bg-pos-card px-3 text-[12.5px] font-semibold text-pos-accent shadow-[0_1px_3px_rgba(60,40,20,.08)]"
              >
                <EditOutlined className="text-[13px]" />
                {t("common.edit")}
              </button>
            )}
            <PartyBlock
              title={t("invoicePreview.recipient")}
              name={invoice.customer_name}
              address={`${invoice.customer_city}, ${invoice.customer_address}`}
              eik={invoice.customer_eik}
              vat={invoice.customer_vat}
              mol={invoice.customer_mol}
            />
            <PartyBlock
              title={t("invoicePreview.supplier")}
              name={invoice.supplier_name}
              address={invoice.supplier_address}
              eik={invoice.supplier_eik}
              vat={invoice.supplier_vat}
              mol={invoice.supplier_mol}
              bankName={invoice.supplier_bank_name}
              bic={invoice.supplier_bic}
              iban={invoice.supplier_iban}
            />
          </div>

          {/* Title */}
          <h2
            style={{
              margin: "18px 0 14px",
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: 0.5,
            }}
          >
            {isCreditNote(invoice)
              ? t("fiscalInvoice.preview.titleCreditNote")
              : invoice.invoice_type === "simplified"
              ? t("invoicePreview.titleSimplified")
              : printAsDuplicate
                ? t("invoicePreview.titleDuplicate")
                : t("invoicePreview.titleOriginal")}
          </h2>

          {/* Document meta row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isNarrow ? "1fr 1fr" : "1fr 1fr 1fr 1fr",
              gap: 14,
              background: "#EDECE8",
              border: "1px solid #C9C7BF",
              padding: "10px 14px",
              marginBottom: 14,
            }}
          >
            <MetaCell
              label={t("invoicePreview.docNumber")}
              value={invoice.invoice_number}
            />
            <MetaCell
              label={t("invoicePreview.docDate")}
              value={formatDate(invoice.issue_date)}
            />
            <MetaCell
              label={t("invoicePreview.supplyDate")}
              value={formatDate(invoice.supply_date)}
            />
            <MetaCell
              label={t("invoicePreview.supplyPlace")}
              value={invoice.supply_place ?? "—"}
            />
          </div>

          {/* Lines table + totals — изнесени (бакшишът е ред без ДДС и
              отделен тотал „Бакшиш (без ДДС)"). */}
          <InvoicePaperLines lines={invoice.lines} breakdown={breakdown} />
          <InvoicePaperTotals breakdown={breakdown} isNarrow={isNarrow} />

          {/* Footer card: получаване | плащане + коментар + съставил */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isNarrow ? "1fr" : "1fr 1fr",
              gap: 0,
              background: "#EDECE8",
              border: "1px solid #C9C7BF",
              padding: 14,
              marginBottom: 30,
            }}
          >
            <div style={{ paddingRight: isNarrow ? 0 : 14 }}>
              <div
                style={{
                  fontSize: 11,
                  color: "#5E5749",
                  marginBottom: 4,
                  fontStyle: "italic",
                }}
              >
                {t("invoicePreview.goodsReceived")}
              </div>
              <KeyVal
                label={t("invoicePreview.receivedAt")}
                value={formatDate(invoice.received_at)}
              />
              <KeyVal
                label={t("invoicePreview.receivedBy")}
                value={invoice.received_by ?? "—"}
              />
              <SignatureLine />
            </div>
            <div style={{ paddingLeft: isNarrow ? 0 : 14 }}>
              <KeyVal
                label={t("invoicePreview.paymentMethod")}
                value={paymentMethodName ?? "—"}
              />
              <KeyVal
                label={t("invoicePreview.noteLabel")}
                value={invoice.note ?? "—"}
              />
              <KeyVal
                label={t("invoicePreview.preparedBy")}
                value={invoice.created_by_name || "—"}
              />
              <SignatureLine />
            </div>
          </div>

          {/* Branding footer — последен child в .invoice-paper.
              В print .invoice-paper е flex column с min-height A4 → този
              блок получава margin-top:auto и се „притиска" към долния ръб
              на последната (или единствената) страница. На екрана просто
              стои в края на документа. */}
          <div
            className="invoice-branding"
            style={{
              marginTop: 18,
              textAlign: "center",
              fontSize: 9.5,
              color: "#5E5749",
              letterSpacing: 0.2,
            }}
          >
            Този документ е отпечатан от софтуер на фирма ЪнриълСофт ООД
            {" — "}
            www.unrealsoft.bg
          </div>
        </div>

        <ModalFooter isNarrow={isNarrow} narrowFirst>
          <GhostButton
            label={t("common.close")}
            onClick={onClose}
            shortcut={isDesktop ? "Esc" : undefined}
          />
          {allowCancel && canInvoice && !invoice.cancelled_at && !fiscalDocument && (
            <DangerButton
              label={t("invoicePreview.cancel")}
              onClick={handleCancelInvoice}
              loading={cancelMutation.isPending}
              icon={<StopOutlined />}
            />
          )}
          {creditable && <FiscalInvoiceCreditAction invoice={invoice} onDone={onClose} />}
          {/* Анулираната софтуерна фактура — протоколът за анулирането ѝ (и по-късно,
              с номера на новата); фискалният документ не се анулира. */}
          {invoice.cancelled_at && !fiscalDocument && (
            <SoftPrimaryButton
              label={t("invoicePreview.printCancellation")}
              onClick={() => cancellationProtocol.print(invoice.id)}
              loading={cancellationProtocol.isPrinting}
              icon={<PrinterOutlined />}
            />
          )}
          {/* Анулирана фактура не се печата — BE-то също я отказва; документ
              от фискалното устройство — също (оригиналът е бонът). */}
          {!invoice.cancelled_at && !fiscalDocument && (
            <SoftPrimaryButton
              label={t("invoicePreview.printPos")}
              onClick={printPos}
              loading={isPrintingPos}
              icon={<PrinterOutlined />}
            />
          )}
          {!fiscalDocument && (
            <PrimaryButton
              label={t("invoicePreview.print")}
              onClick={printA4}
              icon={<PrinterOutlined />}
            />
          )}
        </ModalFooter>
      </div>

      {/* Редакция на получателя — вложен модал; фактурата в prevew-то се
          обновява сама, защото идва от ["invoices", "detail", id] */}
      <InvoiceCustomerEditModal
        open={isEditingCustomer}
        invoice={invoice}
        onClose={() => setIsEditingCustomer(false)}
      />

      {/* След A4 печат на оригинала — браузърът не казва печат ли е или „Отказ" */}
      <PrintPosConfirmModal
        open={originalPrintedQuestion.open}
        loading={originalPrintedQuestion.saving}
        title={t("invoicePreview.originalPrintedTitle")}
        subtitle={t("invoicePreview.originalPrintedDescription")}
        onYes={originalPrintedQuestion.confirm}
        onNo={originalPrintedQuestion.decline}
      />
    </Modal>
  );
};

// ── Helpers ──────────────────────────────────────────────────────────────

function PartyBlock({
  title,
  name,
  address,
  eik,
  vat,
  mol,
  bankName,
  bic,
  iban,
}: {
  title: string;
  name: string;
  address: string | null;
  eik: string;
  vat: string | null;
  mol: string | null;
  bankName?: string | null;
  bic?: string | null;
  iban?: string | null;
}) {
  const hasBank = Boolean(bankName || bic || iban);
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: "#5C5A52",
          fontStyle: "italic",
          marginBottom: 2,
        }}
      >
        {title}:
      </div>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
        {name}
      </div>
      <div style={{ marginBottom: 8 }}>
        <span
          style={{
            fontSize: 11,
            color: "#5C5A52",
            fontStyle: "italic",
            marginRight: 6,
          }}
        >
          Адрес:
        </span>
        <span style={{ fontWeight: 600 }}>{address || "—"}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 6 }}>
        <span style={{ fontStyle: "italic", color: "#5C5A52" }}>ЕИК №:</span>
        <span style={{ fontWeight: 600 }}>{eik}</span>
        <span style={{ fontStyle: "italic", color: "#5C5A52" }}>ДДС №:</span>
        <span style={{ fontWeight: 600 }}>{vat || "—"}</span>
        <span style={{ fontStyle: "italic", color: "#5C5A52" }}>МОЛ:</span>
        <span style={{ fontWeight: 600 }}>{mol || "—"}</span>
        {hasBank && (
          <>
            <span style={{ fontStyle: "italic", color: "#5C5A52" }}>
              Банка:
            </span>
            <span style={{ fontWeight: 600 }}>{bankName || "—"}</span>
            <span style={{ fontStyle: "italic", color: "#5C5A52" }}>BIC:</span>
            <span style={{ fontWeight: 600 }}>{bic || "—"}</span>
            <span style={{ fontStyle: "italic", color: "#5C5A52" }}>IBAN:</span>
            <span style={{ fontWeight: 600, wordBreak: "break-all" }}>
              {iban || "—"}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function MetaCell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: "#5C5A52",
          fontStyle: "italic",
        }}
      >
        {label}:
      </div>
      <div style={{ fontWeight: 700, fontSize: 13.5 }}>{value}</div>
    </div>
  );
}

function KeyVal({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        marginBottom: 4,
        fontSize: 12.5,
      }}
    >
      <span
        style={{
          fontStyle: "italic",
          color: "#5C5A52",
          minWidth: 110,
        }}
      >
        {label}:
      </span>
      {/* pre-line: бележката е на редове — препратките към фискалните бонове
          (BE `invoices/receipt_refs.py`, Н-18), после текстът на оператора. */}
      <span style={{ fontWeight: 600, whiteSpace: "pre-line" }}>{value}</span>
    </div>
  );
}

function SignatureLine() {
  return (
    <div
      style={{
        marginTop: 18,
        fontSize: 11,
        color: "#5C5A52",
        fontStyle: "italic",
      }}
    >
      Подпис ............................................
    </div>
  );
}

export default InvoicePreviewModal;
